import React, { useCallback, useMemo, useState } from 'react';
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import { Box, Button, Flex, Spinner, Text } from '@chakra-ui/react';
import { getGoogleMapApiKey, GOOGLE_MAPS_LIBRARIES, GOOGLE_MAPS_SCRIPT_ID } from '../../Utilities/Apikeys';

const DEFAULT_CENTER = { lat: 12.9716, lng: 77.5946 };
const mapContainerStyle = { width: '100%', height: '280px', borderRadius: '12px' };

const reverseGeocode = (lat, lng) => new Promise((resolve) => {
  if (!window.google?.maps?.Geocoder) {
    resolve('');
    return;
  }
  const geocoder = new window.google.maps.Geocoder();
  geocoder.geocode({ location: { lat, lng } }, (results, status) => {
    if (status === 'OK' && results?.[0]?.formatted_address) {
      resolve(results[0].formatted_address);
      return;
    }
    resolve('');
  });
});

const LocationPicker = ({ value, onChange }) => {
  const apiKey = getGoogleMapApiKey();
  const [mapReady, setMapReady] = useState(false);

  const { isLoaded, loadError } = useJsApiLoader({
    id: GOOGLE_MAPS_SCRIPT_ID,
    googleMapsApiKey: apiKey || '',
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  const center = useMemo(() => {
    if (value?.lat != null && value?.lng != null) {
      return { lat: Number(value.lat), lng: Number(value.lng) };
    }
    return DEFAULT_CENTER;
  }, [value]);

  const markerPosition = useMemo(() => {
    if (value?.lat != null && value?.lng != null) {
      return { lat: Number(value.lat), lng: Number(value.lng) };
    }
    return null;
  }, [value]);

  const updateLocation = useCallback(async (lat, lng) => {
    const address = await reverseGeocode(lat, lng);
    onChange({ lat, lng, address });
  }, [onChange]);

  const handleMapClick = useCallback((event) => {
    if (!event?.latLng) return;
    updateLocation(event.latLng.lat(), event.latLng.lng());
  }, [updateLocation]);

  const handleMarkerDragEnd = useCallback((event) => {
    if (!event?.latLng) return;
    updateLocation(event.latLng.lat(), event.latLng.lng());
  }, [updateLocation]);

  const handleUseCurrentLocation = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (position) => {
        updateLocation(position.coords.latitude, position.coords.longitude);
      },
      () => {},
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  if (!apiKey) {
    return (
      <Box p={4} border="1px solid" borderColor="slate.200" borderRadius="lg" bg="slate.50">
        <Text color="slate.600" fontSize="sm">
          Add your Google Maps API key in Utilities/Apikeys.js to pick a location on the map.
        </Text>
      </Box>
    );
  }

  if (loadError) {
    return (
      <Box p={4} border="1px solid" borderColor="red.200" borderRadius="lg" bg="red.50">
        <Text color="red.600" fontSize="sm">
          Failed to load Google Maps. Check the API key and Maps JavaScript API billing/restrictions.
        </Text>
      </Box>
    );
  }

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={2} gap={3} flexWrap="wrap">
        <Text fontSize="sm" color="slate.600">
          Tap the map or drag the marker to set the service location.
        </Text>
        <Button size="sm" variant="outline" colorScheme="brand" onClick={handleUseCurrentLocation}>
          Use current location
        </Button>
      </Flex>
      <Box
        border="1px solid"
        borderColor="slate.200"
        borderRadius="lg"
        overflow="hidden"
        position="relative"
        minH="280px"
        bg="slate.50"
      >
        {!isLoaded && (
          <Flex position="absolute" inset={0} align="center" justify="center" zIndex={1} gap={2}>
            <Spinner size="sm" color="brand.500" />
            <Text fontSize="sm" color="slate.500">Loading map...</Text>
          </Flex>
        )}
        {isLoaded && (
          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            center={center}
            zoom={markerPosition ? 16 : 12}
            onLoad={() => setMapReady(true)}
            onClick={handleMapClick}
            options={{
              streetViewControl: false,
              mapTypeControl: false,
              fullscreenControl: false,
              clickableIcons: false,
            }}
          >
            {mapReady && markerPosition && (
              <Marker
                position={markerPosition}
                draggable
                onDragEnd={handleMarkerDragEnd}
              />
            )}
          </GoogleMap>
        )}
      </Box>
      {markerPosition && (
        <Text mt={2} fontSize="xs" color="slate.500">
          Selected: {markerPosition.lat.toFixed(6)}, {markerPosition.lng.toFixed(6)}
        </Text>
      )}
    </Box>
  );
};

export default LocationPicker;
